<template>
  <Popover>
    <PopoverButton class="group flex items-center fixed bottom-4 right-4 z-20 bg-secondary p-4 cursor-pointer rounded-full shadow shadow-zinc-900 outline-none transition-all duration-300 ease-in-out" @click="openModal">
      <img alt="AirSage logo" class="h-8 mx-auto" src="@/assets/img/logo_white.png" />
      <div class="text-lg text-zinc-100 max-w-0 overflow-hidden whitespace-nowrap group-hover:max-w-xs group-hover:ml-2 transition-all duration-300 ease-in-out">Insights</div>
    </PopoverButton>
    <Transition
      enter-active-class="transition duration-200 ease-out"
      enter-from-class="translate-y-1 opacity-0"
      enter-to-class="translate-y-0 opacity-100"
      leave-active-class="transition duration-150 ease-in"
      leave-from-class="translate-y-0 opacity-100"
      leave-to-class="translate-y-1 opacity-0"
    >
      <PopoverPanel class="fixed bottom-4 right-4 z-20" v-slot="{ close }">
        <div class="w-[675px] bg-zinc-100 dark:bg-zinc-900 rounded-lg shadow shadow-zinc-900 overflow-hidden">
          <div class="bg-secondary py-2 px-4 flex justify-between place-items-center">
            <h3 class="text-xl font-semibold text-zinc-100 flex space-x-4">
              <img alt="AirSage logo" class="h-7 mx-auto text-zinc-100" src="@/assets/img/logo_white.png" />
              <span>Insights</span>
            </h3>
            <IconClose class="size-6 cursor-pointer" @click="close" />
          </div>
          <div ref="chat" class="p-4 h-96 overflow-y-auto relative">
            <div v-for="(message, index) in messages" :key="index" class="mb-2" :class="{'text-right': message.sender === 'user'}">
              <div class="inline-block rounded-xl space-y-4" :class="{'user bg-accent text-white px-3 py-2': message.sender === 'user', 'bot p-4 bg-zinc-200 dark:bg-zinc-800': message.sender === 'bot'}" v-html="message.text"></div>
            </div>
            <div class="pt-4 flex space-x-1" :class="{'invisible': !pending, 'absolute bottom-4': messages.length < 2}">
              <div class="size-2 bg-zinc-700 rounded-full animate-bounce [animation-delay:-0.3s]"></div>
              <div class="size-2 bg-zinc-700 rounded-full animate-bounce [animation-delay:-0.15s]"></div>
              <div class="size-2 bg-zinc-700 rounded-full animate-bounce"></div>
            </div>
          </div>
          <div class="p-2 flex space-x-2 dark:bg-zinc-800/50">
            <input v-model="newPrompt" @keyup.enter="sendMessage" type="text" placeholder="Type a message..." class="flex-grow py-2 rounded-lg border border-zinc-400 dark:border-zinc-600 hover:bg-zinc-50/5 bg-transparent text-zinc-700 dark:text-zinc-50 placeholder:text-zinc-600 dark:placeholder:text-zinc-300 focus:outline-none px-2 py-1">
            <button class="border border-accent bg-accent/75 hover:bg-accent text-white px-4 rounded-lg disabled:bg-gray-600 disabled:border-gray-600" @click="sendMessage" :disabled="!canSubmit">Send</button>
          </div>
        </div>
      </PopoverPanel>
    </Transition>
  </Popover>
</template>

<script setup>
import { ref, computed, nextTick } from "vue";
import { useRoute } from "vue-router";
import { useAuth0 } from "@auth0/auth0-vue";
import { Popover, PopoverButton, PopoverPanel } from "@headlessui/vue";
import { StudiesService } from "@/services";
import { useAlertStore } from "@/stores/alert";
import IconClose from "@/components/icons/IconClose.vue";

const alertStore = useAlertStore();
const route = useRoute();
const { getAccessTokenSilently } = useAuth0();

const newPrompt = ref("");
const pending = ref(false);
const messages = ref([{
  sender: "bot",
  text: "Hi! Ask me about statistics and insights about this study."
}]);
const canSubmit = computed(() => newPrompt.value.length && !pending.value);
async function sendMessage() {
  try {
    pending.value = true;
    const { id } = route.params;
    const token = await getAccessTokenSilently();
    messages.value.push({
      sender: "user",
      text: newPrompt.value
    });
    const prompt = newPrompt.value;
    newPrompt.value = "";
    const response = await StudiesService.promptAI(id, prompt, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    messages.value.push({
      sender: "bot",
      text: response
    });
  } catch (error) {
    alertStore.alertError(error);
  } finally {
    pending.value = false;
    scrollToBottom();
  }
}

const chat = ref();
async function scrollToBottom() {
  await nextTick();
  chat.value.scrollTop = chat.value.scrollHeight;
}
</script>

<style lang="css">
.bot svg {
  @apply rounded-xl;
}
</style>